import { createElement, lazy, useState, type ComponentType } from "react";

import { loadConsoleSurface } from "../console/load-console";

/**
 * Console section loader map (P-07).
 *
 * Every lazy chunk the provider console needs is named here once. The lazy
 * components (AccessDashboard, App, the work queue's viewer) are made from
 * these loaders with `lazyConsoleChunk`, and the same functions prefetch a
 * section before it is opened: the work queue at idle once Home has content,
 * any section on hover, focus or touch of its nav item, and the remembered
 * section during the console bootstrap (P-01). Each loader is memoised, so a
 * prefetch and the later render share one request, and it remembers the
 * loaded module so a render after a prefetch needs no Suspense round.
 */
export type ChunkLoader<T> = (() => Promise<T>) & {
  /** The module once it has loaded, else undefined. */
  peek: () => T | undefined;
};

export function chunkLoader<T>(load: () => Promise<T>): ChunkLoader<T> {
  let promise: Promise<T> | null = null;
  let loaded: T | undefined;
  const loader = (() => {
    if (!promise) {
      const started = load().then((module) => { loaded = module; return module; });
      promise = started;
      // A failed chunk load (deploy, offline) is retried by the next caller.
      started.catch(() => { if (promise === started) promise = null; });
    }
    return promise;
  }) as ChunkLoader<T>;
  loader.peek = () => loaded;
  return loader;
}

/**
 * React.lazy for a console chunk, except that a chunk already in memory (a
 * prefetch finished) renders in the same pass. React.lazy alone suspends once
 * even for a loaded chunk: the fallback commits for a frame and React then
 * holds the content back for its 300 ms minimum fallback time, which on Home
 * delayed the offers by about 300 ms after their data had arrived (measured).
 * The choice is made once per mount, so a section that started through the
 * lazy path is never remounted (and never loses its state) when the chunk
 * arrives.
 */
export function lazyConsoleChunk<P extends object>(loader: () => Promise<{ default: ComponentType<P> }>): ComponentType<P> {
  const Lazy = lazy(loader);
  const peek = (loader as Partial<ChunkLoader<{ default: ComponentType<P> }>>).peek;
  function ConsoleChunk(props: P) {
    const [loaded] = useState(() => peek?.());
    return createElement((loaded ? loaded.default : Lazy) as ComponentType<P>, props);
  }
  return ConsoleChunk;
}

export const consoleChunks = {
  // ADR-WES1's loader: the ConsoleSurface chunk together with the console
  // message part and the console-only components, so the bootstrap, the
  // sign-in submit and the first-screen preload all share one request set.
  surface: chunkLoader(() => loadConsoleSurface()),
  home: chunkLoader(() => import("../components/ProviderHomePanel")),
  workQueue: chunkLoader(() => import("../components/ProviderWorkQueuePanel")),
  onboarding: chunkLoader(() => import("../components/ProviderOnboardingWizard")),
  operations: chunkLoader(() => import("../components/ProviderOperationsPanel")),
  appPreferences: chunkLoader(() => import("../components/ProviderAppPreferencesPanel")),
  capabilities: chunkLoader(() => import("../components/ProviderCapabilitiesPanel")),
  specialties: chunkLoader(() => import("../components/ProviderSpecialtiesPanel")),
  colorSupport: chunkLoader(() => import("../components/ProviderColorSupportPanel")),
  shippingRates: chunkLoader(() => import("../components/ProviderShippingRatesPanel")),
  rateCardSetup: chunkLoader(() => import("../components/ProviderRateCardSetupPanel")),
  quoteSimulator: chunkLoader(() => import("../components/ProviderQuoteSimulatorPanel")),
  payouts: chunkLoader(() => import("../components/ProviderPayoutsPanel")),
  integrationsInterest: chunkLoader(() => import("../components/ProviderIntegrationsInterestPanel")),
  instantAlerts: chunkLoader(() => import("../components/ProviderInstantAlertsPanel")),
  accountSettings: chunkLoader(() => import("../components/AccountSettingsPanel")),
  /** three.js viewer: loaded when a provider asks for a model preview (P-11). */
  modelViewer: chunkLoader(() => import("../components/ProviderModelViewer"))
};

/** Which chunks each provider console section renders. */
export const providerSectionChunks: Record<string, Array<keyof typeof consoleChunks>> = {
  dashboard: ["home"],
  work_queue: ["workQueue"],
  onboarding: ["onboarding"],
  operations: ["operations", "appPreferences"],
  capabilities: ["capabilities", "specialties", "colorSupport"],
  rate_cards: ["shippingRates", "rateCardSetup", "quoteSimulator"],
  payouts: ["payouts"],
  api_keys: ["integrationsInterest"],
  account: ["instantAlerts", "accountSettings"]
};

/** Starts a section's chunks without rendering it. Never throws. */
export function prefetchProviderSection(section: string): void {
  for (const chunk of providerSectionChunks[section] ?? []) {
    void consoleChunks[chunk]().catch(() => undefined);
  }
}

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void, options: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

/** Runs `task` when the main thread is idle (or soon after). Returns a cancel. */
export function whenIdle(task: () => void, timeout = 2_000): () => void {
  if (typeof window === "undefined") return () => {};
  const browser = window as IdleWindow;
  if (browser.requestIdleCallback) {
    const id = browser.requestIdleCallback(task, { timeout });
    return () => browser.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(task, 200);
  return () => window.clearTimeout(id);
}
